/**
 * SurveyPanel — COGO, traverse, control points → real map layers
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  IconButton,
  Stack,
} from '@mui/material';
import { Add, Delete, Calculate, GpsFixed } from '@mui/icons-material';
import { useAppDispatch, useAppSelector, useAppStore } from '../../../store/hooks';
import { addLayer, setActiveLayer, updateLayer } from '../../../store/slices/layersSlice';
import { addNotification } from '../../../store/slices/uiSlice';
import { cogoService, type CogoResult, type TraversePoint } from '../../../services/CogoService';
import { mapNavigator } from '../../../services/MapNavigator';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index} style={{ paddingTop: 8 }}>
    {value === index && children}
  </div>
);

const SurveyPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const layers = useAppSelector((s) => s.layers.layers);
  const [tab, setTab] = useState(0);

  const [cogoMode, setCogoMode] = useState<'bearing-distance' | 'intersection-bb'>(
    'bearing-distance'
  );
  const [cogoInput, setCogoInput] = useState({
    fromX: '',
    fromY: '',
    bearing: '',
    distance: '',
    bearing1: '',
    bearing2: '',
    x2: '',
    y2: '',
  });
  const [cogoResult, setCogoResult] = useState<CogoResult | null>(null);
  const [traversePoints, setTraversePoints] = useState<TraversePoint[]>([
    {
      id: '1',
      station: '100',
      northing: 0,
      easting: 0,
      elevation: 100,
      description: 'Start',
    },
  ]);
  const [traverseClosure, setTraverseClosure] = useState<ReturnType<
    typeof cogoService.computeTraverseClosure
  > | null>(null);
  const [controlPoints, setControlPoints] = useState<
    Array<{ id: string; name: string; e: number; n: number; z?: number }>
  >([]);
  const [ctrlForm, setCtrlForm] = useState({ name: '', e: '', n: '', z: '' });

  const addPointToMap = useCallback(
    (x: number, y: number, props: Record<string, unknown>, layerName = 'COGO Points') => {
      const currentLayers = store.getState().layers.layers;
      const current = currentLayers.find(
        (layer) =>
          layer.type === 'vector' && layer.geometryType === 'Point' && layer.name === layerName
      );
      const layerId = current?.id ?? `survey-pts-${Date.now()}`;
      const feature = {
        id: `pt-${Date.now()}`,
        layerId,
        geometry: { type: 'Point' as const, coordinates: [x, y] },
        properties: props,
      };

      if (current) {
        const features = [...(current.features || []), feature];
        dispatch(
          updateLayer({
            id: layerId,
            updates: { features, featureCount: features.length },
          })
        );
      } else {
        dispatch(
          addLayer({
            id: layerId,
            name: layerName,
            type: 'vector',
            geometryType: 'Point',
            visible: true,
            opacity: 1,
            zIndex: currentLayers.length + 1,
            featureCount: 1,
            features: [feature],
          })
        );
      }
      dispatch(setActiveLayer(layerId));
      mapNavigator.zoomToLonLat(x, y, 18);
      return feature;
    },
    [dispatch, store]
  );

  const runCogo = useCallback(() => {
    let result: CogoResult | null = null;

    if (cogoMode === 'bearing-distance') {
      const from = { x: parseFloat(cogoInput.fromX), y: parseFloat(cogoInput.fromY) };
      const dist = parseFloat(cogoInput.distance);
      const bearing = parseFloat(cogoInput.bearing);
      if ([from.x, from.y, dist, bearing].every((n) => isFinite(n))) {
        result = cogoService.bearingDistance(from, bearing, dist);
      }
    } else {
      const p1 = { x: parseFloat(cogoInput.fromX), y: parseFloat(cogoInput.fromY) };
      const p2 = { x: parseFloat(cogoInput.x2), y: parseFloat(cogoInput.y2) };
      const b1 = parseFloat(cogoInput.bearing1);
      const b2 = parseFloat(cogoInput.bearing2);
      if ([p1.x, p1.y, p2.x, p2.y, b1, b2].every((n) => isFinite(n))) {
        result = cogoService.bearingBearingIntersection(p1, b1, p2, b2);
      }
    }
    setCogoResult(result);
    if (result && !result.success) {
      dispatch(addNotification({ type: 'error', message: result.message }));
    }
  }, [cogoMode, cogoInput, dispatch]);

  const placeCogoOnMap = () => {
    if (!cogoResult?.success || !cogoResult.point) {
      dispatch(addNotification({ type: 'warning', message: 'Run COGO successfully first' }));
      return;
    }
    addPointToMap(cogoResult.point.x, cogoResult.point.y, {
      source: 'COGO',
      method: cogoMode,
      note: cogoResult.message,
    });
    dispatch(addNotification({ type: 'success', message: 'COGO point added to map' }));
  };

  const computeTraverse = () => {
    if (traversePoints.length < 2) {
      dispatch(addNotification({ type: 'warning', message: 'Add at least 2 traverse stations' }));
      return;
    }
    const closure = cogoService.computeTraverseClosure(traversePoints);
    setTraverseClosure(closure);
    dispatch(
      addNotification({
        type: 'info',
        message: `Misclosure ${closure.linearMisclosure.toFixed(3)} m · 1:${Math.round(
          closure.relativePrecision || 0
        )}`,
      })
    );
  };

  const saveTraverseAsLayer = () => {
    if (traversePoints.length < 2) return;
    const id = `traverse-${Date.now()}`;
    const coords = traversePoints.map((p) => [p.easting, p.northing]);
    const lineFeature = {
      id: `${id}-line`,
      layerId: id,
      geometry: { type: 'LineString' as const, coordinates: coords },
      properties: {
        type: 'traverse',
        misclosure: traverseClosure?.linearMisclosure ?? null,
        stations: traversePoints.length,
      },
    };
    const pointFeatures = traversePoints.map((p, i) => ({
      id: `${id}-pt${i}`,
      layerId: id,
      geometry: { type: 'Point' as const, coordinates: [p.easting, p.northing] },
      properties: {
        station: p.station,
        description: p.description || '',
        elevation: p.elevation,
        type: 'traverse-station',
      },
    }));

    // Line layer
    dispatch(
      addLayer({
        id,
        name: `Traverse ${new Date().toLocaleTimeString()}`,
        type: 'vector',
        geometryType: 'LineString',
        visible: true,
        opacity: 1,
        zIndex: layers.length + 1,
        featureCount: 1,
        features: [lineFeature],
        symbology: {
          type: 'simple',
          strokeColor: '#e91e63',
          strokeWidth: 2.5,
          opacity: 1,
        },
      })
    );
    // Stations layer
    const ptsId = `${id}-stations`;
    dispatch(
      addLayer({
        id: ptsId,
        name: 'Traverse stations',
        type: 'vector',
        geometryType: 'Point',
        visible: true,
        opacity: 1,
        zIndex: layers.length + 2,
        featureCount: pointFeatures.length,
        features: pointFeatures.map((f) => ({ ...f, layerId: ptsId })),
      })
    );
    dispatch(setActiveLayer(id));
    window.setTimeout(() => {
      mapNavigator.zoomToAllLayers([
        {
          id,
          name: 't',
          type: 'vector',
          visible: true,
          opacity: 1,
          zIndex: 1,
          features: [lineFeature],
        },
      ]);
    }, 150);
    dispatch(addNotification({ type: 'success', message: 'Traverse saved as map layers' }));
  };

  const addControlPoint = () => {
    const e = parseFloat(ctrlForm.e);
    const n = parseFloat(ctrlForm.n);
    const z = ctrlForm.z ? parseFloat(ctrlForm.z) : undefined;
    if (!ctrlForm.name.trim() || !isFinite(e) || !isFinite(n)) {
      dispatch(addNotification({ type: 'warning', message: 'Name, Easting, Northing required' }));
      return;
    }
    const cp = {
      id: String(Date.now()),
      name: ctrlForm.name.trim(),
      e,
      n,
      z: isFinite(z as number) ? z : undefined,
    };
    setControlPoints((prev) => [...prev, cp]);
    addPointToMap(
      e,
      n,
      {
        name: cp.name,
        type: 'control',
        elevation: cp.z,
        source: 'survey-control',
      },
      'Control Points'
    );
    setCtrlForm({ name: '', e: '', n: '', z: '' });
    dispatch(addNotification({ type: 'success', message: `Control “${cp.name}” on map` }));
  };

  const saveControlNetwork = () => {
    if (controlPoints.length === 0) return;
    const id = `control-net-${Date.now()}`;
    const features = controlPoints.map((c, i) => ({
      id: `${id}-f${i}`,
      layerId: id,
      geometry: { type: 'Point' as const, coordinates: [c.e, c.n] },
      properties: { name: c.name, elevation: c.z, type: 'control' },
    }));
    dispatch(
      addLayer({
        id,
        name: 'Control network',
        type: 'vector',
        geometryType: 'Point',
        visible: true,
        opacity: 1,
        zIndex: layers.length + 1,
        featureCount: features.length,
        features,
      })
    );
    dispatch(setActiveLayer(id));
    dispatch(addNotification({ type: 'success', message: 'Control network layer created' }));
  };

  return (
    <Box sx={{ p: 1, fontSize: 13 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
        <GpsFixed color="primary" fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          SURVEY TOOLS
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        sx={{ mb: 1, minHeight: 32 }}
      >
        <Tab label="COGO" sx={{ minHeight: 32, fontSize: 12 }} />
        <Tab label="Traverse" sx={{ minHeight: 32, fontSize: 12 }} />
        <Tab label="Control" sx={{ minHeight: 32, fontSize: 12 }} />
        <Tab label="Stakeout" sx={{ minHeight: 32, fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel>Method</InputLabel>
          <Select
            value={cogoMode}
            label="Method"
            onChange={(e) => setCogoMode(e.target.value as typeof cogoMode)}
          >
            <MenuItem value="bearing-distance">Bearing + Distance</MenuItem>
            <MenuItem value="intersection-bb">Bearing–Bearing intersection</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <TextField
            size="small"
            label="From East (X)"
            value={cogoInput.fromX}
            onChange={(e) => setCogoInput({ ...cogoInput, fromX: e.target.value })}
          />
          <TextField
            size="small"
            label="From North (Y)"
            value={cogoInput.fromY}
            onChange={(e) => setCogoInput({ ...cogoInput, fromY: e.target.value })}
          />
          {cogoMode === 'bearing-distance' ? (
            <>
              <TextField
                size="small"
                label="Bearing ° (from N)"
                value={cogoInput.bearing}
                onChange={(e) => setCogoInput({ ...cogoInput, bearing: e.target.value })}
              />
              <TextField
                size="small"
                label="Distance"
                value={cogoInput.distance}
                onChange={(e) => setCogoInput({ ...cogoInput, distance: e.target.value })}
              />
            </>
          ) : (
            <>
              <TextField
                size="small"
                label="Bearing 1 °"
                value={cogoInput.bearing1}
                onChange={(e) => setCogoInput({ ...cogoInput, bearing1: e.target.value })}
              />
              <TextField
                size="small"
                label="Point 2 East"
                value={cogoInput.x2}
                onChange={(e) => setCogoInput({ ...cogoInput, x2: e.target.value })}
              />
              <TextField
                size="small"
                label="Point 2 North"
                value={cogoInput.y2}
                onChange={(e) => setCogoInput({ ...cogoInput, y2: e.target.value })}
              />
              <TextField
                size="small"
                label="Bearing 2 °"
                value={cogoInput.bearing2}
                onChange={(e) => setCogoInput({ ...cogoInput, bearing2: e.target.value })}
              />
            </>
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<Calculate />}
            onClick={runCogo}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Calculate
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!cogoResult?.success}
            onClick={placeCogoOnMap}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Place on map
          </Button>
        </Stack>

        {cogoResult && (
          <Alert severity={cogoResult.success ? 'success' : 'error'} sx={{ mt: 1.5, fontSize: 12 }}>
            {cogoResult.message}
            {cogoResult.point && (
              <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                E {cogoResult.point.x.toFixed(4)} · N {cogoResult.point.y.toFixed(4)}
              </Box>
            )}
          </Alert>
        )}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Enter station coordinates (Easting / Northing). Compute closure, then save as layers.
        </Typography>
        {traversePoints.map((p, idx) => (
          <Box
            key={p.id}
            sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 0.5, mb: 0.5 }}
          >
            <TextField
              size="small"
              label="Sta"
              value={p.station}
              onChange={(e) => {
                const next = [...traversePoints];
                next[idx] = { ...p, station: e.target.value };
                setTraversePoints(next);
              }}
            />
            <TextField
              size="small"
              label="East"
              value={p.easting}
              onChange={(e) => {
                const next = [...traversePoints];
                next[idx] = { ...p, easting: parseFloat(e.target.value) || 0 };
                setTraversePoints(next);
              }}
            />
            <TextField
              size="small"
              label="North"
              value={p.northing}
              onChange={(e) => {
                const next = [...traversePoints];
                next[idx] = { ...p, northing: parseFloat(e.target.value) || 0 };
                setTraversePoints(next);
              }}
            />
            <IconButton
              size="small"
              disabled={traversePoints.length <= 1}
              onClick={() => setTraversePoints(traversePoints.filter((x) => x.id !== p.id))}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => {
              const last = traversePoints[traversePoints.length - 1];
              setTraversePoints([
                ...traversePoints,
                {
                  id: String(Date.now()),
                  station: String(Number(last.station) + 1 || traversePoints.length + 1),
                  northing: last.northing + 10,
                  easting: last.easting + 10,
                  elevation: last.elevation,
                  description: '',
                },
              ]);
            }}
            sx={{ textTransform: 'none' }}
          >
            Add station
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={computeTraverse}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Compute closure
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={saveTraverseAsLayer}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Save to map
          </Button>
        </Stack>
        {traverseClosure && (
          <Alert severity="info" sx={{ mt: 1, fontSize: 12 }}>
            Length {traverseClosure.totalLength.toFixed(2)} m · ΔE{' '}
            {traverseClosure.deltaE.toFixed(3)} · ΔN {traverseClosure.deltaN.toFixed(3)} ·
            Misclosure {traverseClosure.linearMisclosure.toFixed(3)} m
            {traverseClosure.relativePrecision
              ? ` · 1:${Math.round(traverseClosure.relativePrecision)}`
              : ''}
          </Alert>
        )}
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
          <TextField
            size="small"
            label="Name"
            value={ctrlForm.name}
            onChange={(e) => setCtrlForm({ ...ctrlForm, name: e.target.value })}
          />
          <TextField
            size="small"
            label="Elev (opt)"
            value={ctrlForm.z}
            onChange={(e) => setCtrlForm({ ...ctrlForm, z: e.target.value })}
          />
          <TextField
            size="small"
            label="Easting"
            value={ctrlForm.e}
            onChange={(e) => setCtrlForm({ ...ctrlForm, e: e.target.value })}
          />
          <TextField
            size="small"
            label="Northing"
            value={ctrlForm.n}
            onChange={(e) => setCtrlForm({ ...ctrlForm, n: e.target.value })}
          />
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            onClick={addControlPoint}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Add control
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={controlPoints.length === 0}
            onClick={saveControlNetwork}
            sx={{ textTransform: 'none' }}
          >
            Save network layer
          </Button>
        </Stack>
        {controlPoints.length > 0 && (
          <Table size="small" sx={{ mt: 1 }}>
            <TableBody>
              {controlPoints.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>
                    {c.e.toFixed(2)}, {c.n.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <Alert severity="info" sx={{ fontSize: 12, mb: 1 }}>
          Stakeout uses control / COGO points. Export a stake list as a Point layer with station
          codes.
        </Alert>
        <Button
          fullWidth
          size="small"
          variant="contained"
          disabled={controlPoints.length === 0 && !cogoResult?.point}
          onClick={() => {
            if (controlPoints.length) saveControlNetwork();
            else if (cogoResult?.point) placeCogoOnMap();
          }}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Export stake list to map
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Tip: switch to Field panel for live GNSS stakeout capture.
        </Typography>
      </TabPanel>
    </Box>
  );
};

export default SurveyPanel;
